import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/pro-regular-svg-icons';

import { loginThunk, resetThunk } from '../../actions/login/OrganizationsLoginActions';
import Button from '../UI/V3/Button/Button';
import { Input } from '../UI/V3/Forms/Input/Input';
import { Checkbox } from '../UI/V3/Forms/Checkbox/Checkbox';
import { Logo } from '../UI/Layout/Logo';
import { RootState } from '../../store';
import { cleanAll } from '../../actions/ActionUtils';
import { useAuth } from '../../hooks';
import styles from './LoginComponent.module.scss';

export const LoginComponent: React.FC = () => {
    const dispatch = useDispatch();
    const location = useLocation();
    const auth = useAuth();
    const { loading, error } = useSelector((state: RootState) => state.login);

    const [showPassword, setShowPassword] = useState(false);
    const [rememberMe, setRememberMe] = useState(false);
    const [email, setEmail] = useState({
        value: '',
        error: ''
    });
    const [password, setPassword] = useState({
        value: '',
        error: ''
    });

    useEffect(() => {
        cleanAll(dispatch, true); // Avoid fetching master data on login page
        dispatch(resetThunk());
        document.title = 'Organizations Login - Cuddles';
    }, [dispatch]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Validate fields
        const emailError = validateEmail(email.value);
        const passwordError = validatePassword(password.value);

        setEmail(prev => ({ ...prev, error: emailError }));
        setPassword(prev => ({ ...prev, error: passwordError }));

        // If there are validation errors, don't submit
        if (emailError || passwordError) {
            return;
        }

        const emailValue = email.value.trim().toLowerCase();
        const passwordValue = password.value;

        dispatch(loginThunk(emailValue, passwordValue));
    };

    const handleEmailChange = (value: string) => {
        setEmail({
            value,
            error: value.length === 0 ? '' : validateEmail(value)
        });
    };

    const handlePasswordChange = (value: string) => {
        setPassword({
            value,
            error: value.length === 0 ? '' : validatePassword(value)
        });
    };

    const validateEmail = (value: string): string => {
        if (!value.trim()) {
            return 'Email is required';
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) {
            return 'Please enter a valid email address';
        }
        return '';
    };

    const validatePassword = (value: string): string => {
        if (!value.trim()) {
            return 'Password is required';
        }
        return '';
    };

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    // Check if user is authenticated
    const isAuthenticated = auth && auth.signedIn && auth.user && auth.token;

    if (isAuthenticated) {
        const from = (location.state as any)?.from?.pathname || '/dashboard';
        return <Redirect to={from} />;
    }

    return (
        <div className={styles.loginContainer}>
            {/* Left Panel - Login Form */}
            <div className={styles.leftPanel}>
                <div className={styles.formContainer}>
                    {/* Logo */}
                    <div className={styles.logoContainer}>
                        <Logo />
                    </div>

                    {/* Header */}
                    <div className={styles.header}>
                        <h2>Welcome back!</h2>
                        <p>Enter your email and password to access your account.</p>
                    </div>

                    {/* Error Message */}
                    {error && (
                        <div
                            className={styles.errorMessage}
                            role="alert"
                        >
                            {error}
                        </div>
                    )}

                    {/* Login Form */}
                    <form
                        className={styles.form}
                        onSubmit={handleSubmit}
                    >
                        <div className={styles.fieldGroup}>
                            <Input
                                id="email"
                                type="email"
                                label="Email"
                                placeholder="Enter your email address"
                                value={email.value}
                                error={email.error}
                                onChange={e => handleEmailChange(e.target.value)}
                                autoComplete="on"
                                disabled={loading}
                                required
                            />
                        </div>

                        <div className={styles.fieldGroup}>
                            <div className={styles.passwordInputContainer}>
                                <Input
                                    id="password"
                                    type={showPassword ? 'text' : 'password'}
                                    label="Password"
                                    placeholder="Enter your password"
                                    value={password.value}
                                    error={password.error}
                                    onChange={e => handlePasswordChange(e.target.value)}
                                    autoComplete="on"
                                    disabled={loading}
                                    required
                                />
                                <button
                                    type="button"
                                    className={styles.passwordToggle}
                                    onClick={togglePasswordVisibility}
                                    tabIndex={-1}
                                    disabled={loading}
                                >
                                    <FontAwesomeIcon
                                        icon={showPassword ? faEyeSlash : faEye}
                                        size="sm"
                                    />
                                </button>
                            </div>
                        </div>

                        {/* Remember Me & Forgot Password */}
                        <div className={styles.rememberForgotRow}>
                            <Checkbox
                                label="Remember me"
                                checked={rememberMe}
                                onChange={checked => setRememberMe(checked)}
                            />

                            <a
                                href="#"
                                className={styles.forgotLink}
                            >
                                Forgot your password?
                            </a>
                        </div>

                        {/* Login Button */}
                        <div className={styles.loginButtonContainer}>
                            <Button
                                type="submit"
                                variant="black"
                                size="large"
                                disabled={loading}
                                className={styles.loginButton}
                                label={loading ? 'Signing in...' : 'Login'}
                            ></Button>
                        </div>
                    </form>
                </div>
            </div>

            {/* Right Panel - Image */}
            <div className={styles.rightPanel}>
                <img
                    src="/images/login/login-image.jpg"
                    alt="Login"
                />
            </div>
        </div>
    );
};

export default LoginComponent;
